import { SubscribeResult } from '../typings';
import { InvalidEnvironmentError } from '../errors/InvalidEnvironmentError';
import { NotificationPermissionError, NotificationPermissionErrorReason } from '../errors/NotificationPermissionError';
import { SubscriptionStateError, SubscriptionStateErrorReason } from '../errors/SubscriptionStateError';
import { Logger } from '../helpers/Logger';
import { NetworkRequest } from '../helpers/NetworkRequest';
import { NotificationPermissionStatus } from '../models/NotificationPermission';
import { WindowEnvironmentKind } from '../models/WindowEnvironmentKind';
import { urlBase64ToUint8Array } from '../helpers/Encoding';
import { AdapterConfig } from '../models/AdapterConfig';
import { PermissionManager } from './PermissionManager';
import { ServiceWorkerManager } from './ServiceWorkerManager';
import { Environment } from './Environment';
import { isString } from '@/src/helpers/utils';
import { Subscription } from '@/src/typings';

let applicationServerKey: Uint8Array | null = null;

export class SubscriptionManager {
    static async requestNotificationPermission(): Promise<NotificationPermission> {
        let permissionResult: NotificationPermission | null;

        try {
            permissionResult = await window.Notification.requestPermission();
        } catch (error) {
            throw new NotificationPermissionError(NotificationPermissionErrorReason.Unknown);
        }

        return permissionResult;
    }

    static async getCurrentSubscription(): Promise<PushSubscription | null> {
        const swRegistration: ServiceWorkerRegistration = await ServiceWorkerManager.waitForReady();
        const { pushManager }: { pushManager: PushManager } = swRegistration;
        const subscription: PushSubscription | null = await pushManager.getSubscription();

        return subscription;
    }

    /**
     * Initialize a new push subscription.
     */
    static async subscribe(config: AdapterConfig): Promise<SubscribeResult | null> {
        let result: SubscribeResult | null = null;

        if (PermissionManager.isDenied()) {
            Logger.debug(
                '[Sidetalk][Push][SubscriptionManager]: Unable to subscribe because the browser permission was blocked.',
                config,
            );
            throw new NotificationPermissionError(NotificationPermissionErrorReason.Denied);
        }

        switch (Environment.getEnvironmentKind()) {
            case WindowEnvironmentKind.ServiceWorker:
                result = await SubscriptionManager.subscribeFromWorker();
                break;
            case WindowEnvironmentKind.Host:
                result = await SubscriptionManager.subscribeFromHost();
                break;
            default:
                throw new InvalidEnvironmentError();
        }

        return result;
    }

    static async unsubscribe(config: AdapterConfig): Promise<void> {
        Logger.debug('[Sidetalk][Push][SubscriptionManager]: Wait for service worker to be ready...');
        const workerRegistration: ServiceWorkerRegistration = await ServiceWorkerManager.waitForReady();

        const subscription: PushSubscription | null = await workerRegistration.pushManager.getSubscription();
        if (!subscription) {
            return Promise.resolve();
        }

        const successful: boolean = await this.doPushUnsubscribe(subscription);
        if (!successful) {
            // The unsubscribe was unsuccessful, but we can remove the subscriptionId from our server
            // and notifications will stop
            // This just may be in a bad state when the user returns
            Logger.error(
                '[Sidetalk][Push][SubscriptionManager]: We were unable to unregister from push, maybe already unsubscribed',
                config,
            );
        }

        return Promise.resolve();
    }

    static async subscribeFromWorker(): Promise<null> {
        // TODO: add subscribe action from service worker
        return null;
    }

    static async subscribeFromHost(): Promise<SubscribeResult | null> {
        if (PermissionManager.isDefault()) {
            const permission: NotificationPermission = await SubscriptionManager.requestNotificationPermission();

            switch (permission) {
                case NotificationPermissionStatus.Default:
                    Logger.debug(
                        '[Sidetalk][Push][SubscriptionManager]: Unable to subscribe because the browser permission was dismissed.',
                    );
                    throw new NotificationPermissionError(NotificationPermissionErrorReason.Dismissed);
                case NotificationPermissionStatus.Denied:
                    Logger.debug(
                        '[Sidetalk][Push][SubscriptionManager]: Unable to subscribe because the browser permission was blocked.',
                    );
                    throw new NotificationPermissionError(NotificationPermissionErrorReason.Denied);
                default:
                    break;
            }
        }

        Logger.debug('[Sidetalk][Push][SubscriptionManager]: Wait for service worker to be ready...');
        const workerRegistration: ServiceWorkerRegistration = await ServiceWorkerManager.waitForReady();
        Logger.debug(
            '[Sidetalk][Push][SubscriptionManager]: Service worker is ready and we should continue subscribing.',
        );

        return await this.doPushSubscribe(workerRegistration.pushManager);
    }

    static async getApplicationServerKey(): Promise<Uint8Array> {
        const { WebPush } = window.Sidetalk;

        if (applicationServerKey) {
            return Promise.resolve(applicationServerKey);
        }

        return NetworkRequest.get(WebPush.config.getAPIKeyUrl()).then((response) => {
            const urlBase64Str: string = isString(response.data) ? String(response.data) : '';
            applicationServerKey = urlBase64ToUint8Array(urlBase64Str);

            return Promise.resolve(applicationServerKey);
        });
    }

    static async doPushSubscribe(pushManager: PushManager): Promise<SubscribeResult | null> {
        const key: Uint8Array = await SubscriptionManager.getApplicationServerKey();

        if (!key) {
            throw new SubscriptionStateError(SubscriptionStateErrorReason.InvalidApplicationServerKey);
        }

        const subscriptionOptions: PushSubscriptionOptions = {
            userVisibleOnly: true,
            applicationServerKey: key,
        };

        Logger.debug(
            '[Sidetalk][Push][SubscriptionManager]: : Subscribing to Web Push API with options:',
            subscriptionOptions,
        );

        let oldSubscription: PushSubscription | null = null;
        let subscription: PushSubscription | null = null;
        try {
            oldSubscription = await pushManager.getSubscription();
            subscription = await pushManager.subscribe(subscriptionOptions);
        } catch (error) {
            if ((error as Error)?.name === 'InvalidStateError') {
                // Registration failed - A subscription with a different applicationServerKey already exists;
                //    to change the applicationServerKey, unsubscribe then resubscribe.
                Logger.warn(
                    '[Sidetalk][Push][SubscriptionManager]: : Could not subscribe due to applicationServerKey ' +
                        'changing unsubscribe and attempting to subscribe with new key.',
                    error,
                );

                oldSubscription = await pushManager.getSubscription();
                await SubscriptionManager.doPushUnsubscribe(oldSubscription);

                subscription = await pushManager.subscribe(subscriptionOptions);
                oldSubscription = null;
            } else {
                throw error; // If some other error, bubble the exception up
            }
        }

        return {
            subscription,
            oldSubscription,
        };
    }

    static async doPushUnsubscribe(subscription?: PushSubscription | null): Promise<boolean> {
        if (!subscription) {
            return false;
        }

        Logger.debug('[Sidetalk][Push][SubscriptionManager]: Unsubscribing existing push subscription.');
        const successful: boolean = await subscription.unsubscribe();
        Logger.debug(
            `[Sidetalk][Push][SubscriptionManager]: Unsubscribing existing push subscription result: ${successful}`,
        );

        return successful;
    }

    static parseRawSubscription(subscription?: PushSubscription | null): Subscription | null {
        if (!subscription) {
            return null;
        }

        return JSON.parse(JSON.stringify(subscription));
    }
}
